import React, { useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import Hls from 'hls.js';
import { Oval } from 'react-loader-spinner';

import { PlayIcon } from '../icons';
import styles from './ClipPlayer.module.scss';

const HLS_TYPE = 'application/vnd.apple.mpegurl';

type ClipPlayerProps = {
  src: string;
  posterUrl: string;
  onProgress?: (progress: number) => void;
  seekTo?: number | null; // 0..1
};

type HTMLEnhancedVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen: () => void;
};

export default function ClipPlayer({
  src,
  posterUrl,
  onProgress,
  seekTo,
}: ClipPlayerProps) {
  const videoRef = useRef<HTMLEnhancedVideoElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if (video.canPlayType(HLS_TYPE) || !Hls.isSupported()) {
      video.src = src;
      return;
    }

    const hls = new Hls();
    hls.loadSource(src);
    hls.attachMedia(video);

    return () => hls.destroy();
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || seekTo == null || !isFinite(video.duration)) return;
    video.currentTime = Math.max(0, Math.min(1, seekTo)) * video.duration;
  }, [seekTo]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !onProgress || !isFinite(v.duration) || v.duration === 0) return;
    onProgress(v.currentTime / v.duration);
  };

  const handleClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.muted) {
      v.muted = false;
      setIsMuted(false);
      return;
    }
    if (v.paused) {
      v.play();
    } else {
      v.pause();
    }
  };

  return (
    <div className={styles.player}>
      {posterUrl && (
        <div
          className={styles.backdrop}
          style={{ backgroundImage: `url(${posterUrl})` }}
          aria-hidden
        />
      )}
      <div className={styles.stage} onClick={handleClick}>
        <video
          ref={videoRef}
          className={styles.video}
          preload="auto"
          poster={posterUrl}
          muted
          playsInline
          autoPlay
          loop
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPaused(false)}
          onPause={() => setIsPaused(true)}
          onCanPlay={() => setIsReady(true)}
          onWaiting={() => setIsReady(false)}
          onVolumeChange={() =>
            setIsMuted(videoRef.current?.muted ?? true)
          }
        >
          <source type={HLS_TYPE} src={src} />
        </video>

        <div
          className={classnames(styles.playOverlay, {
            [styles.visible]: isPaused && isReady,
          })}
          aria-hidden
        >
          <PlayIcon size={72} />
        </div>

        {isMuted && (
          <div className={styles.mutedHint} aria-hidden>
            Tap to unmute
          </div>
        )}

        {!isReady && (
          <div className={styles.loader} aria-hidden>
            <Oval color="#ffffff" secondaryColor="#ffffff55" height={48} width={48} />
          </div>
        )}
      </div>
    </div>
  );
}
